import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Redirect, RouteComponentProps } from 'react-router';
import { withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { Formik, FormikHelpers } from 'formik';
import _ from 'lodash';
import {
  Section,
  LoadingImage,
  Text,
  TextOptions,
  Footer,
  Svg,
  GetTradingBlockSiteName,
  Errors,
} from '@tradingblock/components';
import {
  AllApplicationSteps,
  ApplicationStep,
  ApplicationModel,
  DefaultApplicationModel,
  BaseFormProps,
  MismatchedDataModalOptions,
  ApplicationType,
  BooleanToggleType,
} from '../types';
import {
  getApplicationStepUrl,
  updateApplication,
  getForcedOffRampUrl,
  validateApplicationStepAsync,
  checkForMismatchedDataType,
  delayedScrollTo,
  isFinalApplicationSteps,
  isApplicationStepInvalid,
  canAccessApplicationStep,
  isApplicationComplete,
} from '../services';
import { useApplication, useStore } from '../context';
import { PageTitle } from '../components';
import { ApplicationLoader, ApplicationFormComponents } from '../components/application';
import { ApplicationSubmissionLoader } from '../components/ApplicationSubmissionLoader';
import { useData, useStateSelector } from '../state/selectors';
import { useDispatcher } from '../components/hooks';
import { UiActions, ApplicationActions } from '../state/actions';
import config from '../config';
import { ErrorScrollWrapper } from '../components/ErrorScrollWrapper';
import { shouldRemoveClearerOption } from '../hooks/useApplicationSteps';

interface ApplyPageProps extends RouteComponentProps<{ step: ApplicationStep }>, LocalizeContextProps {}

const ApplyPage: React.FC<ApplyPageProps> = props => {
  const {
    match: { params },
  } = props;

  const storage = useStore();
  const dispatch = useDispatcher();
  const [{ isFetching }, { setFormSubmitted, setAccountType }] = useApplication();

  const application = useData(d => d.data.application);
  const createdAccount = useData(d => d.data.createdAccount);
  const { numberOfSections, visibleSections, scrollToSection } = useData(d => d.ui.sectionInfo);
  const { isSaving, saveType, saveStep } = useData(d => d.data.saveInfo);
  const createErrorCode = useData(d => d.data.createErrorCode);
  const step = useData(d => d.ui.step);
  const nextPage = useData(d => d.ui.nextPage);
  const clearer = application && application.clearer;
  const [{ mismatchedDataModal }] = useApplication();

  const stepIndex = useMemo(() => _.indexOf(AllApplicationSteps, step), [step]);
  const isOnLastSectionOfStep = useMemo(
    () => !!numberOfSections && !!visibleSections && visibleSections >= numberOfSections,
    [numberOfSections, visibleSections]
  );

  // Scroll to top when step changes (e.g., after user creation redirect from Home to Country/How did you hear about screen)
  useEffect(() => {
    if (step) {
      const id = setTimeout(() => {
        window.scrollTo(0, 0);
      }, 50);
      return () => clearTimeout(id);
    }
  }, [step]);

  // try to update step from url
  useEffect(() => {
    const nextStep = _.includes(AllApplicationSteps, params.step) ? params.step : undefined;
    if (!nextStep) {
      dispatch(UiActions.setNextPage('/'));
    } else if (nextStep !== step && !_.isUndefined(isFetching) && !isFetching && !nextPage) {
      // Don't update step from URL if we just finished saving on the current step - let the save navigation effect handle it
      // This prevents race conditions where application updates trigger this effect before navigation completes
      // Match the exact condition from the save navigation effect (lines 145-169) to avoid interference
      // Also ensure step is defined to avoid blocking initial load when step and saveStep are both undefined
      if (step && !isSaving && saveType && saveStep === step) {
        // Save navigation effect is waiting to run - don't interfere
        return;
      }
      if (
        application &&
        isFinalApplicationSteps(nextStep) &&
        canAccessApplicationStep(nextStep, application) &&
        isApplicationStepInvalid(nextStep, application)
      ) {
        // force user to review errors on summary page if can access next step but it's invalid
        dispatch(UiActions.setNextPage(getApplicationStepUrl('summary')));
      } else if (application && !canAccessApplicationStep(nextStep, application)) {
        // if can't access step, send back to last saved step
        dispatch(
          UiActions.setNextPage(getApplicationStepUrl(application.latestStepSaved || ApplicationStep.SecureAccount))
        );
      } else {
        dispatch(UiActions.setStep({ step: nextStep, application: application || DefaultApplicationModel }));
      }
    }
  }, [params, application, isFetching, step, nextPage, dispatch, isSaving, saveType, saveStep]);

  // scroll to next visible section
  useEffect(() => {
    if (visibleSections && visibleSections > 1 && scrollToSection) {
      const section = document.getElementById(`${step}-section-${visibleSections}`);
      if (section) {
        // Scroll to the section, accounting for fixed headers and ensuring previous section is out of view
        setTimeout(() => {
          const rect = section.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

          // Get header height from CSS variable or calculate from header element
          const headerHeightStr = getComputedStyle(document.documentElement)
            .getPropertyValue('--header-actual-height')
            .trim();
          let headerHeight = 0;
          if (headerHeightStr) {
            headerHeight = parseFloat(headerHeightStr.replace('px', ''));
          } else {
            const headerElement = document.querySelector('.header');
            if (headerElement) {
              headerHeight = headerElement.getBoundingClientRect().height;
            }
          }

          // Calculate target position: section top - header height - small buffer to ensure previous section is out of view
          const buffer = 20; // pixels to ensure previous section is completely scrolled past
          const targetPosition = rect.top + scrollTop - headerHeight - buffer;

          window.scrollTo({ behavior: 'smooth', left: 0, top: Math.max(0, targetPosition) });
        }, 10);
      }
    }
  }, [step, visibleSections, scrollToSection]);

  const getNextPage = useCallback(
    (sendToNextPage?: boolean) => {
      if (saveType === 'partial') {
        return getApplicationStepUrl('summary');
      } else if (saveType === 'continue' || saveType === 'confirm-data-mismatch') {
        if (sendToNextPage) {
          // if next step is agreements, send to review/summary first
          if (stepIndex + 1 === _.indexOf(AllApplicationSteps, ApplicationStep.Agreements)) {
            return getApplicationStepUrl('summary');
          } else if (step && isFinalApplicationSteps(step) && isApplicationStepInvalid(step, application)) {
            // send user to summary to review errors if current step isn't invalid
            return getApplicationStepUrl('summary');
          } else if (step === ApplicationStep.SecureAccount && clearer) {
            return getApplicationStepUrl(ApplicationStep.AccountType);
          } else {
            return getApplicationStepUrl(AllApplicationSteps[stepIndex + 1]);
          }
        }
      }
      return undefined;
    },
    [stepIndex, saveType, step, application]
  );

  useEffect(() => {
    if (!isSaving && saveType && saveStep === step && !nextPage) {
      let nextUrl = getNextPage();
      if (saveType === 'continue' || saveType === 'confirm-data-mismatch') {
        // check for any off ramp conditions
        const forcedNextUrl = getForcedOffRampUrl(application);

        // only move past current step if on last section and no errors
        nextUrl = getNextPage(isOnLastSectionOfStep);
        if (forcedNextUrl) {
          nextUrl = forcedNextUrl;
        } else if (!nextUrl) {
          // if staying on same step, increment # of visible sections
          dispatch(UiActions.showNextSection({ scrollToSection: true }));
          // reset form submitted flag
          setFormSubmitted(false);
        }
      }

      if (nextUrl) {
        dispatch(UiActions.setNextPage(nextUrl));
        // reset form submitted flag
        setFormSubmitted(false);
      }
    }
  }, [
    application,
    step,
    nextPage,
    saveStep,
    saveType,
    isSaving,
    isOnLastSectionOfStep,
    getNextPage,
    setFormSubmitted,
    dispatch,
  ]);

  useEffect(() => {
    if (!isSaving && saveType === 'complete') {
      if (createdAccount) {
        // save application to storage
        if (application) {
          dispatch(ApplicationActions.requestSaveApplication({ application, saveType, storage }));
        }

        setAccountType(ApplicationType.Individual);

        // send to status page
        dispatch(UiActions.setNextPage('/status'));
      } else if (_.isUndefined(createdAccount) && application && isApplicationComplete(application)) {
        // create the TB account after application saved and complete
        if (application.isPartialEntitySubmission) {
          dispatch(ApplicationActions.requestCreatePartialEntityAccount({ application, storage }));
        } else {
          dispatch(ApplicationActions.requestCreateAccount({ application, storage }));
        }
      }
    }
  }, [saveType, isSaving, createdAccount, application, dispatch, storage]);

  // if already created account, don't allow access to any apply form steps
  if (application && application.accountId) {
    return <Redirect to="/status" />;
  }

  // Show loader during final form submission process (CreateAccount endpoint)
  // Only show for 'complete' save type, not for 'continue' (partial submissions)
  const isSubmittingApplication =
    isSaving && saveType === 'complete' && !createErrorCode && _.isUndefined(mismatchedDataModal);

  return (
    <ApplicationLoader allowAnonymous={step === ApplicationStep.SecureAccount}>
      {step && <PageTitle page={step} />}
      {isSubmittingApplication ? <ApplicationSubmissionLoader isVisible={true} /> : <ApplyForm {...props} />}
    </ApplicationLoader>
  );
};

const ApplyForm: React.FC<ApplyPageProps> = ({ match, setActiveLanguage }) => {
  const storage = useStore();
  const dispatch = useDispatcher();
  const [
    { mismatchedDataModal, formSubmitted, accountType, entityPdfApplicationLinksClicked },
    { setMismatchedDataModal, setFormSubmitted },
  ] = useApplication();

  const authToken = useData(d => d.ui.apiToken);
  const application = useData(d => d.data.application);
  const { numberOfSections, visibleSections } = useData(d => d.ui.sectionInfo);
  const { isSaving, saveType } = useData(d => d.data.saveInfo);
  const createErrorCode = useData(d => d.data.createErrorCode);
  const step = useData(d => d.ui.step);
  const nextPage = useData(d => d.ui.nextPage);
  const userRequirements = useData(d => d.ui.userRequirements);
  const siteGrpParam = process.env.REACT_APP_TB_SITE_GROUP;
  const stepIndex = useMemo(() => _.indexOf(AllApplicationSteps, step), [step]);
  const isOnLastSectionOfStep = useMemo(
    () => !!numberOfSections && !!visibleSections && visibleSections >= numberOfSections,
    [numberOfSections, visibleSections]
  );
  const [showEntityPdfApplicationLinksClickedErrorMsg, setShowEntityPdfApplicationLinksClickedErrorMsg] = useState<
    boolean
  >(false);

  const initialValues = useMemo(() => {
    if (application && !_.isEmpty(application)) {
      // The entityAccountType key is getting set on the application object if not defined to make sure existing applications
      // in progress will allow for validation errors to be shown on the entity account type field.
      // Set default tradeAuthorization to "No" if not defined to avoid confusion.
      return {
        ...application,
        ...(!application.entityAccountType && { entityAccountType: null }),
        ...(application.tradeAuthorization === undefined || application.tradeAuthorization === null
          ? { tradeAuthorization: BooleanToggleType.No }
          : {}),
      };
    }
    return DefaultApplicationModel;
  }, [application]);

  const closeMismatchedDataModal = useCallback(
    (options?: MismatchedDataModalOptions) => {
      const mismatchedDataType = mismatchedDataModal && mismatchedDataModal.type;
      // use false to mark as closed
      setMismatchedDataModal(false);
      // save application to confirm data mismatch and complete current step
      if (step && mismatchedDataType && options && options.confirmMismatchedData && options.formValues) {
        const applicationWithConfirmation = {
          ...options.formValues,
          confirmedMismatchedDataTypes: [
            mismatchedDataType,
            ...(options.formValues.confirmedMismatchedDataTypes || []),
          ],
        };
        // update status fields on application
        const data = updateApplication(step, false, true, applicationWithConfirmation, visibleSections);
        dispatch(
          ApplicationActions.requestSaveApplication({
            application: data,
            saveType: 'confirm-data-mismatch',
            saveStep: step,
            storage,
          })
        );
      } else if (options && options.scrollToTop) {
        delayedScrollTo(0);
      }
    },
    [mismatchedDataModal, step, visibleSections, setMismatchedDataModal, dispatch, storage]
  );

  const getStepProps = (step: ApplicationStep, props: BaseFormProps<ApplicationModel>) => ({
    ...props,
    getText: (id: string, textKey?: string, options?: TextOptions) => {
      const textOptions = {
        ...options,
        data: {
          ...(options && options.data),
          siteName: GetTradingBlockSiteName(undefined, siteGrpParam),
        },
      };
      return <Text id={id} page={step} textKey={textKey} {...textOptions} />;
    },
    setActiveLanguage,
    mismatchedDataModal,
    closeMismatchedDataModal,
    formSubmitted,
  });

  // build id of submit btn, used for translation id
  const submitButtonId = useMemo(() => {
    if (step === ApplicationStep.Beneficiaries) {
      return 'review';
    }

    if (
      step === ApplicationStep.Sign ||
      (config.isVirtual && isOnLastSectionOfStep) ||
      (accountType === ApplicationType.Entity && step === ApplicationStep.AccountType)
    ) {
      return 'submit';
    }

    return 'continue';
  }, [step, isOnLastSectionOfStep, accountType]);

  const duplicateAccountCheckState = useStateSelector(s => s.data.duplicateAccountCheck);

  // handle validate
  const onValidate = (values: ApplicationModel) => {
    // console.warn('ApplyPage :: onValidate ', step, visibleSections, values);
    // if already logged in, only validate first section of secure acct step
    const visibleSectionsToValidate = authToken && step === ApplicationStep.SecureAccount ? 1 : visibleSections;
    return validateApplicationStepAsync(
      step,
      values,
      visibleSectionsToValidate,
      userRequirements,
      duplicateAccountCheckState
    );
  };

  // handle submit
  const onSubmit = (values: ApplicationModel, formikHelpers: FormikHelpers<ApplicationModel>) => {
    // once valid form submitted, reset touched fields
    formikHelpers.setTouched({});

    if (
      values.type === ApplicationType.Entity &&
      entityPdfApplicationLinksClicked === false &&
      step === ApplicationStep.AccountType
    ) {
      setShowEntityPdfApplicationLinksClickedErrorMsg(true);
      return;
    }

    setShowEntityPdfApplicationLinksClickedErrorMsg(false);

    // submitting virtual application (first step of application only)
    if (step === ApplicationStep.SecureAccount && isOnLastSectionOfStep && config.isVirtual) {
      dispatch(ApplicationActions.requestCreateUser({ application: values }));
    } else {
      const mismatchedDataType = isOnLastSectionOfStep ? checkForMismatchedDataType(values, step) : undefined;
      if (mismatchedDataType) {
        setMismatchedDataModal({ type: mismatchedDataType });
      } else {
        // save and continue
        saveStepAndContinue(values);
      }
    }
  };

  const saveStepAndContinue = async (values: ApplicationModel) => {
    // check for any off ramp conditions
    const forcedNextUrl = getForcedOffRampUrl(values);
    // check if current step is valid
    const isStepValid = step && !isApplicationStepInvalid(step, values);
    // can only move past current step if on last section and no offramp url
    const isStepCompleted = isOnLastSectionOfStep && isStepValid && !forcedNextUrl;
    // check if we should skip clearer and set it to APEX
    const shouldSkipClearer = shouldRemoveClearerOption(values.hearAboutUsId, values.repId, heardAbout);

    // User creation now happens on the home page, so we don't need this logic here
    // if (!authToken && isStepCompleted && step === ApplicationStep.SecureAccount) {
    //   dispatch(ApplicationActions.requestCreateUser({ application: values }));
    //   return;
    // }

    // reset mismatched data modal state
    if (mismatchedDataModal === false) {
      setMismatchedDataModal(undefined);
    }

    // If we should skip clearer step, then we need to set clearer to apex.
    if (shouldSkipClearer.shouldRemoveClearerOption && shouldSkipClearer.clearerType) {
      values = {
        ...values,
        clearer: shouldSkipClearer.clearerType,
      };
    } else if (step === ApplicationStep.SecureAccount && !shouldSkipClearer.shouldRemoveClearerOption) {
      // On every update to this page, we will need to ask what commission style they want.
      values = {
        ...values,
        clearer: null,
      };
    }

    // update status fields on application
    const data = updateApplication(
      step ||
        (step === ApplicationStep.AccountType &&
          values.type === ApplicationType.Entity &&
          ApplicationStep.AccountType) ||
        ApplicationStep.ClearerType, // Need to force the next step to be clearer
      !isStepCompleted,
      isStepCompleted,
      values,
      visibleSections,
      step === ApplicationStep.AccountType && values.type === ApplicationType.Entity ? true : false
    );
    // if completing last step, use saveType = 'complete'
    const saveType =
      isStepCompleted && stepIndex + 1 === (values.type === ApplicationType.Entity ? 3 : AllApplicationSteps.length)
        ? 'complete'
        : 'continue';
    // save application
    dispatch(ApplicationActions.requestSaveApplication({ application: data, saveType, saveStep: step, storage }));
  };

  const onSaveForLater = async (values: ApplicationModel) => {
    // update status fields on application with incrementSection = false, isStepCompleted = false
    const data = updateApplication(step || ApplicationStep.SecureAccount, false, false, values, visibleSections);
    // save application with saveType = 'partial'
    dispatch(
      ApplicationActions.requestSaveApplication({ application: data, saveType: 'partial', saveStep: step, storage })
    );
  };

  const onHandleSubmit = (handler: () => void, e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // set submitted flag
    setFormSubmitted(true);
    // fire formik handler
    handler();
  };

  const heardAbout = useData(d => d.ui.heardAbout);

  useEffect(() => {
    if (
      step !== ApplicationStep.AccountType ||
      accountType !== ApplicationType.Entity ||
      (accountType === ApplicationType.Entity &&
        step === ApplicationStep.AccountType &&
        entityPdfApplicationLinksClicked) ||
      entityPdfApplicationLinksClicked === null
    ) {
      setShowEntityPdfApplicationLinksClickedErrorMsg(false);
    }
  }, [accountType, step, entityPdfApplicationLinksClicked]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={onValidate}
      validateOnBlur={false}
    >
      {(props: BaseFormProps<ApplicationModel>) => (
        <ErrorScrollWrapper>
          <form onSubmit={e => onHandleSubmit(props.handleSubmit, e)} autoComplete="on">
            <Section>
              <div className={`step-title${step === ApplicationStep.ClearerType ? ' step-title--service-level' : ''}`}>
                <h1>{step && <Text id={`step-title${config.isVirtual ? '-virtual' : ''}`} page={step} />}</h1>
              </div>
            </Section>

            {_.map(
              ApplicationFormComponents,
              (Step: any, key: ApplicationStep) =>
                step === key && (
                  <div key={`step${key}`}>
                    <Step {...getStepProps(key, props)} />
                  </div>
                )
            )}

            {showEntityPdfApplicationLinksClickedErrorMsg && (
              <div className="error-wrapper">
                <div className="form-content">
                  <p className="error" style={{ display: 'flex', justifyContent: 'center' }}>
                    <Text id="mustClickEntityPdfApplicationLinksProvided" type="error" />
                  </p>
                </div>
              </div>
            )}

            {accountType === ApplicationType.Entity && createErrorCode && !isSaving && (
              <div className="error-wrapper">
                <div className="form-content">
                  <Errors
                    errors={{
                      accountCreateErrorMsg: <Text id="problemCompletingOnlineRegistrationProcess" type="error" />,
                    }}
                  />
                </div>
              </div>
            )}

            {step !== ApplicationStep.ClearerType && (
              <Footer
                primaryPanel={
                  <button
                    className={`btn-unified btn-unified--primary btn-unified--full ${
                      isSaving ? 'btn-unified--loading' : ''
                    }`}
                    type="submit"
                    disabled={!!isSaving}
                    aria-label={isSaving ? 'Processing, please wait' : `Submit ${submitButtonId}`}
                  >
                    {isSaving ? (
                      <>
                        <span className="visually-hidden">Processing, please wait</span>
                        <span aria-hidden="true">Processing...</span>
                      </>
                    ) : (
                      <>
                        <Text id={`btns.${submitButtonId}`} />
                        <span className="tb-icon ms-2" aria-hidden="true">
                          <Svg path="icon-arrow" />
                        </span>
                      </>
                    )}
                  </button>
                }
                secondaryPanel={
                  <button
                    className={`btn-unified btn-unified--outline btn-unified--full ${
                      isSaving ? 'btn-unified--loading' : ''
                    }`}
                    type="button"
                    disabled={!!isSaving}
                    onClick={() => onSaveForLater(props.values)}
                    tabIndex={-1}
                    aria-label={isSaving ? 'Saving for later, please wait' : 'Save your progress for later'}
                  >
                    {isSaving ? (
                      <>
                        <span className="visually-hidden">Saving for later, please wait</span>
                        <span aria-hidden="true">Saving...</span>
                      </>
                    ) : (
                      <Text id="btns.save-for-later" />
                    )}
                  </button>
                }
              />
            )}

            {step === ApplicationStep.AccountType && props.values.type !== ApplicationType.Entity && (
              <div className="form-content" style={{ marginTop: '1rem', paddingTop: '1rem' }}>
                <p className="mb-0 pb-0 txt-sm">
                  <Text id="review-documents" textKey="description" page="account" />
                </p>
              </div>
            )}
          </form>
        </ErrorScrollWrapper>
      )}
    </Formik>
  );
};

export default withLocalize(ApplyPage);
